import { ChevronLeft, ChevronRight, MessagesSquare } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useChatList } from '@services/chat/queries/useChatList'

import { cn } from '@/lib/utils'
import { ChatConversationItem } from './ChatConversationItem'

const PAGE_SIZE = 8

export function ChatConversationList({
  onSelect,
  user
}: {
  onSelect: (id?: string) => void
  user?: string
}) {
  const { t } = useTranslation('chat')
  const [isActive, setIsActive] = useState(true)
  const [page, setPage] = useState(0)

  // Always pass `user` (even undefined) so the request uses the /chats endpoint
  // that the backend scopes to the current user AND filters by is_active. The
  // /chats/me branch ignores is_active, which broke the Open/Closed tabs.
  const { data, isLoading } = useChatList({
    isActive,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    user
  })

  const results = data?.results ?? []
  const count = data?.count ?? 0
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE))

  const switchTab = (active: boolean) => {
    setIsActive(active)
    setPage(0)
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Tabs */}
      <div className="flex shrink-0 gap-1 p-3">
        {[
          { active: true, label: t('form.common.open') },
          { active: false, label: t('form.common.closed') }
        ].map((tab) => (
          <button
            key={tab.label}
            type="button"
            onClick={() => switchTab(tab.active)}
            className={cn(
              'flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors',
              isActive === tab.active
                ? 'bg-primary text-white'
                : 'text-ink/60 hover:bg-ink/[0.05]'
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-3 pb-3">
        {isLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded-2xl bg-ink/[0.04]"
            />
          ))
        ) : results.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
            <MessagesSquare className="h-10 w-10 text-ink/20" />
            <p className="text-sm text-muted-foreground">
              {t('form.common.no_chats')}
            </p>
          </div>
        ) : (
          results.map((chat) => (
            <ChatConversationItem
              key={chat.id}
              item={chat}
              onClick={onSelect}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-4 border-t border-ink/[0.06] p-2">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink/70 transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-muted-foreground">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages - 1}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="flex h-8 w-8 items-center justify-center rounded-full text-ink/70 transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}
